import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  allVoiTemplates as allVoiTemplatesState,
  unitSystem as unitSystemState,
} from '@/components/Analysis/state';
import { BLISTER_TYPE, NOT_APPLICABLE_LABEL, PIT_TYPE, UNCERTAIN_PROFILE_LABEL } from '@/constants';
import {
  formatEnumForDisplay,
  titleCaseSentence,
  formatTimeStamps,
  formatFieldValue,
} from '@/utils';
import { enumToString } from '@/utils/string';
import { Columns, PointOfInterestsQueryType } from '@/types';
import { UnitsEnum, UnitSystemEnum, VisibilityEnum } from '@/__generated__/graphql';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';
import { useGetStructureId } from '@/hooks/useGetStructureId';

export type RowData = {
  id: string;
  poiId: string;
  name: string;
  assembly: string;
  assemblyId: string;
  visibility: string;
  paintRegion: string;
  comment: string;
  isVerified: string;
  accessibility: string;
  status: string;
  isManuallyCreated: string;
  imageName: string;
  imageLink: string;
  createdAt: string;
  dueDate: string;
  isUncontextualized: boolean;
  fov: number;
  centerPoint3d: string;
  [key: string]: string | number | boolean;
};

type PoiRowProps = {
  pointOfInterests: PointOfInterestsQueryType;
  unitSystem: UnitSystemEnum;
  // getUnit: (value: number | undefined, percentage: number | undefined) => string;
  structureId: string;
  allVoiTemplates: string[];
};

type PoiColsProps = {
  columns: Columns[];
  hiddenColumns: string[];
  rows: RowData[];
};

type PoiDataProps = {
  columns: Columns[];
  hiddenColumns: string[];
  pointOfInterests: PointOfInterestsQueryType;
};

const generateRows = ({
  pointOfInterests,
  unitSystem,
  structureId,
  allVoiTemplates,
}: PoiRowProps): RowData[] => {
  return (pointOfInterests ?? []).map((pointOfInterest) => {
    const {
      id,
      name,
      poiId,
      type: poiType,
      status,
      template,
      templateData,
      visibility,
      paintRegion,
      comment,
      isVerified,
      accessibility,
      blister,
      assembly: { id: assemblyId, tagName },
      isManuallyCreated,
      location,
      centerPoint3d,
      createdAt,
      dueDate,
      isGoverning,
    } = pointOfInterest;

    const { x, y, z } = centerPoint3d ?? {};
    const centerPoint3dExists = ![x, y, z].includes(undefined);

    const isUncontextualized = !centerPoint3dExists;
    const isVoi = allVoiTemplates.some(
      (voiTemplate) => voiTemplate.toLowerCase() === enumToString(poiType).toLowerCase()
    );
    const imageViewExists = location?.name;

    const { name: templateName = '', fields: templateFields = [] } = template ?? {};
    const rowData: RowData = {
      id,
      isGoverning: isGoverning === undefined ? 'N/A' : isGoverning ? 'Yes' : 'No',
      type: templateName,
      poiId: poiId || NOT_APPLICABLE_LABEL,
      name: name || NOT_APPLICABLE_LABEL,
      assembly: tagName,
      assemblyId,
      visibility: titleCaseSentence(visibility ?? VisibilityEnum.Public),
      paintRegion: paintRegion?.name ?? '',
      comment: comment ?? '',
      isVerified: isVerified ? 'true' : 'false',
      accessibility: formatEnumForDisplay(accessibility),
      status: formatEnumForDisplay(status),
      isManuallyCreated: isManuallyCreated ? 'true' : 'false',
      imageName: location?.name ?? NOT_APPLICABLE_LABEL,
      imageLink: NOT_APPLICABLE_LABEL,
      createdAt: formatTimeStamps(createdAt ?? '', 'dd/MMM/yyyy'),
      dueDate: formatTimeStamps(dueDate ?? '', 'dd/MMM/yyyy'),
      isUncontextualized,
      fov: 0,
      centerPoint3d: centerPoint3dExists ? JSON.stringify([x, y, z]) : '',
    };

    if (!isUncontextualized) {
      const fov = isVoi ? 1 : imageViewExists ? 0 : 2;
      rowData.fov = fov;
      const lookAt = `&look.x=${x}&look.y=${y}&look.z=${z}`;
      const position = `&position.x=${imageViewExists ? x : 0}&position.y=${
        imageViewExists ? y : 0
      }&position.z=${imageViewExists ? z : 0}`;
      rowData.imageLink = `${window.location.origin}/analysis/${structureId}/viewer?assembly=${rowData.assembly}&poi=${rowData.id}${lookAt}${position}&fov=${fov}`;
      if (imageViewExists) rowData.imageLink += `&selectedSpherical=${rowData.imageName}`;
    }
    templateData?.forEach(({ id: fieldId, value: fieldValue }) => {
      if (!fieldId || !fieldValue) return;
      const fieldUnit = templateFields.find((item) => item.id === fieldId)?.unit;
      rowData[fieldId] = formatFieldValue({
        unitSystem,
        type: undefined,
        unit: fieldUnit ?? undefined,
        value: fieldValue,
        showUnitLabel: true,
      });
    });

    if ((poiType === BLISTER_TYPE || poiType === PIT_TYPE) && blister) {
      const { maxHeight, wallLoss, residualCorrosionAllowance, residualThickness } = blister;

      // for maximum blister height
      rowData.maxHeight = localiseMeasurement({
        unitSystem,
        value: !isNaN(Number(maxHeight)) ? Number(maxHeight) : undefined,
        sourceUnit: UnitsEnum.Meter,
        displayAsUnit: UnitsEnum.Millimeter,
        fallbackLabel: UNCERTAIN_PROFILE_LABEL,
      });

      rowData.estimatedWallLoss =
        localiseMeasurement({
          unitSystem,
          value: Number(wallLoss?.value),
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          showUnitLabel: false,
        }) + formatPercentage(Number(wallLoss?.percentage));

      rowData.residualCorrosionAllowance =
        localiseMeasurement({
          unitSystem,
          value: Number(residualCorrosionAllowance?.value),
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          showUnitLabel: false,
        }) + formatPercentage(Number(residualCorrosionAllowance?.percentage));

      // for residual thickness
      rowData.residualThickness =
        localiseMeasurement({
          unitSystem,
          value: Number(residualThickness?.value),
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          showUnitLabel: false,
        }) + formatPercentage(Number(residualThickness?.percentage));
    }

    return rowData;
  });
};

const generateCols = ({ columns, rows, hiddenColumns }: PoiColsProps) => {
  if (rows) {
    return columns
      .filter(
        (column) =>
          !column.visibility || (column.visibility && !hiddenColumns?.includes(column?.key))
      )
      .map((col) => ({
        label: col.columnName ?? 'no label',
        data: rows.map((row) => row[col.key] || ''),
      }));
  }
  return [];
};

const usePoiData = ({ pointOfInterests, columns, hiddenColumns }: PoiDataProps) => {
  const unitSystem = useAtomValue(unitSystemState);
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);
  const structureId = useGetStructureId();

  const rows = useMemo(
    () => generateRows({ pointOfInterests, unitSystem, structureId, allVoiTemplates }),
    [pointOfInterests, unitSystem, structureId, allVoiTemplates]
  );

  const cols = useMemo(
    () => generateCols({ columns, rows, hiddenColumns }),
    [columns, rows, hiddenColumns]
  );

  return { rows, cols };
};

export default usePoiData;
